/**
 * Frontend Logging Service
 * Provides structured logging for React frontend application
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogContext {
  component?: string;
  method?: string;
  userId?: string;
  sessionId?: string;
  correlationId?: string;
  operation?: string;
  traceId?: string;
  spanId?: string;
  [key: string]: any;
}

export interface ErrorContext extends LogContext {
  errorCode?: string;
  errorType?: string;
  stackTrace?: string;
  userAgent?: string;
  url?: string;
  httpMethod?: string;
  statusCode?: number;
}

export interface LogConfig {
  level: LogLevel;
  enableConsole: boolean;
  enableExternal: boolean;
  context: string;
}

class FrontendLogger {
  private config: LogConfig;
  private sessionId: string;
  private correlationId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.correlationId = this.generateCorrelationId();
    this.config = {
      level: this.getLogLevel(),
      enableConsole: true,
      enableExternal: false, // Future integration with external services
      context: "frontend",
    };
  }

  private getLogLevel(): LogLevel {
    // In test environment, only show errors to reduce noise
    if (
      typeof import.meta !== "undefined" &&
      import.meta.env?.MODE === "test"
    ) {
      return "error";
    }
    // In production, only show warnings and errors
    if (typeof import.meta !== "undefined" && import.meta.env?.PROD) {
      return "warn";
    }
    // In development, show all logs
    return "debug";
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateCorrelationId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 9);
    return `corr_${timestamp}_${random}`;
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ["debug", "info", "warn", "error"];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const messageLevelIndex = levels.indexOf(level);
    return messageLevelIndex >= currentLevelIndex;
  }

  private formatMessage(
    level: LogLevel,
    message: string,
    context?: LogContext,
    meta?: any,
  ): string {
    const timestamp = new Date().toISOString();
    const contextStr = context?.component ? `[${context.component}]` : "";
    const methodStr = context?.method ? `.${context.method}` : "";

    return `${timestamp} ${level.toUpperCase()} ${contextStr}${methodStr} ${message}`;
  }

  private createLogObject(
    level: LogLevel,
    message: string,
    context?: LogContext,
    meta?: any,
  ) {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: {
        ...context,
        sessionId: this.sessionId,
        userAgent:
          typeof navigator !== "undefined"
            ? navigator.userAgent
            : "test-environment",
        url:
          typeof window !== "undefined"
            ? window.location.href
            : "test-environment",
      },
      meta,
    };
  }

  private log(
    level: LogLevel,
    message: string,
    context?: LogContext,
    meta?: any,
  ): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const logObject = this.createLogObject(level, message, context, meta);

    if (this.config.enableConsole) {
      const formattedMessage = this.formatMessage(
        level,
        message,
        context,
        meta,
      );

      switch (level) {
        case "debug":
          console.debug(formattedMessage, meta || "");
          break;
        case "info":
          console.info(formattedMessage, meta || "");
          break;
        case "warn":
          console.warn(formattedMessage, meta || "");
          break;
        case "error":
          console.error(formattedMessage, meta || "");
          break;
      }
    }

    // Future: Send to external logging service
    if (this.config.enableExternal) {
      this.sendToExternalService(logObject);
    }
  }

  private sendToExternalService(logObject: any): void {
    // Future implementation for external logging services
    // Could integrate with services like Sentry, LogRocket, etc.
  }

  // Public logging methods
  debug(message: string, context?: LogContext, meta?: any): void {
    this.log("debug", message, context, meta);
  }

  info(message: string, context?: LogContext, meta?: any): void {
    this.log("info", message, context, meta);
  }

  warn(message: string, context?: LogContext, meta?: any): void {
    this.log("warn", message, context, meta);
  }

  error(message: string, context?: LogContext, meta?: any): void {
    this.log("error", message, context, meta);
  }

  // Specialized logging methods
  apiCall(
    method: string,
    url: string,
    status?: number,
    duration?: number,
    context?: LogContext,
    meta?: any,
  ): void {
    this.info(
      `API ${method} ${url}${status ? ` ${status}` : ""}${duration ? ` - ${duration}ms` : ""}`,
      {
        ...context,
        operation: "api",
        method,
        url,
        status,
        duration,
      },
      meta,
    );
  }

  graphqlOperation(
    operation: string,
    duration?: number,
    context?: LogContext,
    meta?: any,
  ): void {
    const message = duration
      ? `GraphQL ${operation} completed in ${duration}ms`
      : `GraphQL ${operation} started`;

    this.info(
      message,
      {
        ...context,
        operation: "graphql",
        graphqlOperation: operation,
        duration,
      },
      meta,
    );
  }

  userAction(action: string, context?: LogContext, meta?: any): void {
    this.info(
      `User action: ${action}`,
      {
        ...context,
        operation: "user_action",
        action,
      },
      meta,
    );
  }

  performance(
    operation: string,
    duration: number,
    context?: LogContext,
    meta?: any,
  ): void {
    this.info(
      `Performance: ${operation} completed in ${duration}ms`,
      {
        ...context,
        operation: "performance",
        duration,
      },
      meta,
    );
  }

  errorBoundary(error: Error, errorInfo: any, context?: LogContext): void {
    this.error(`Error boundary caught error: ${error.message}`, {
      ...context,
      operation: "error_boundary",
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      errorInfo,
    });
  }

  // Enhanced error logging methods
  logError(error: Error, context?: ErrorContext, meta?: any): void {
    const errorContext: ErrorContext = {
      correlationId: this.correlationId,
      errorCode: error.name,
      errorType: error.constructor.name,
      stackTrace: error.stack,
      userAgent:
        typeof navigator !== "undefined"
          ? navigator.userAgent
          : "test-environment",
      url:
        typeof window !== "undefined"
          ? window.location.href
          : "test-environment",
      ...context,
    };

    this.error(error.message, errorContext, {
      ...meta,
      type: "error",
    });
  }

  logApiError(
    error: Error,
    request: { method: string; url: string; data?: any },
    response?: { status: number; statusText: string },
    context?: LogContext,
  ): void {
    const errorContext: ErrorContext = {
      correlationId: this.correlationId,
      errorCode: error.name,
      errorType: "API_ERROR",
      stackTrace: error.stack,
      httpMethod: request.method,
      url: request.url,
      statusCode: response?.status,
      userAgent:
        typeof navigator !== "undefined"
          ? navigator.userAgent
          : "test-environment",
      ...context,
    };

    this.error(`API Error: ${error.message}`, errorContext, {
      request: {
        method: request.method,
        url: request.url,
        data: request.data,
      },
      response: response
        ? {
            status: response.status,
            statusText: response.statusText,
          }
        : undefined,
      type: "api_error",
    });
  }

  logGraphQLError(
    error: Error,
    operation?: string,
    variables?: any,
    context?: LogContext,
  ): void {
    const errorContext: ErrorContext = {
      correlationId: this.correlationId,
      errorCode: error.name,
      errorType: "GRAPHQL_ERROR",
      stackTrace: error.stack,
      userAgent:
        typeof navigator !== "undefined"
          ? navigator.userAgent
          : "test-environment",
      url:
        typeof window !== "undefined"
          ? window.location.href
          : "test-environment",
      ...context,
    };

    this.error(`GraphQL Error: ${error.message}`, errorContext, {
      operation,
      variables,
      type: "graphql_error",
    });
  }

  // Correlation ID management
  getCorrelationId(): string {
    return this.correlationId;
  }

  generateNewCorrelationId(): string {
    this.correlationId = this.generateCorrelationId();
    return this.correlationId;
  }

  setCorrelationId(correlationId: string): void {
    this.correlationId = correlationId;
  }

  // Context management
  withContext(additionalContext: LogContext): FrontendLogger {
    const newLogger = new FrontendLogger();
    newLogger.config = { ...this.config };
    newLogger.sessionId = this.sessionId;

    // Override log method to include additional context
    const originalLog = newLogger.log.bind(newLogger);
    newLogger.log = (
      level: LogLevel,
      message: string,
      context?: LogContext,
      meta?: any,
    ) => {
      const mergedContext = { ...additionalContext, ...context };
      originalLog(level, message, mergedContext, meta);
    };

    return newLogger;
  }

  // Configuration methods
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  enableConsole(enable: boolean): void {
    this.config.enableConsole = enable;
  }

  enableExternal(enable: boolean): void {
    this.config.enableExternal = enable;
  }
}

// Create singleton instance
export const logger = new FrontendLogger();

// Export the class for testing
export { FrontendLogger };
